import React, { useCallback, useEffect, useState } from "react";
import "./Login.css";
import WhiteLabelService from "../../services/whitelabel";
import { LoadingButton } from "@mui/lab";
import {
  Chip,
  FormControl,
  Grid2 as Grid,
  MenuItem,
  Select,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import Profile from "../../components/Profile";
import ReactCountryFlag from "react-country-flag";
import { getCountryName } from "../../common/helpers/utils";
import countries from "../../common/const/countries";
import {
  ChallengeType,
  Country,
  Customization,
  LoginProxy,
  LoginState,
  LoginType,
  ProfileData,
} from "../../common/types";
import ExtensionLogin from "../../components/ExtensionLogin";
import { toast } from "react-toastify";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import LockIcon from "@mui/icons-material/Lock";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { getAccountFromToken } from "../../common/helpers/http";
import LoginExtension from "../../services/loginExtension";
import * as Sentry from "@sentry/react";

interface ILoginForm {
  email: string;
  password: string;
  currentProxy: string;
  pin: string;
  phoneNumber: string;
  phoneCountry: string;
}

const wsBaseUrl = import.meta.env.VITE_WS_URL;

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [loginState, setLoginState] = useState(LoginState.LOGIN);
  const [loginType, setLoginType] = useState(LoginType.EMAIL);
  const [challengeType, setChallengeType] = useState<ChallengeType>();
  const [customization, setCustomization] = useState<Customization>();
  const [profileData, setProfileData] = useState<ProfileData>();
  const [tokenProfileData, setTokenProfileData] = useState<ProfileData>();
  const [proxies, setProxies] = useState<LoginProxy>({
    country_code: "",
    countries: [],
    custom_proxies: [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isConnectingAccount, setIsConnectingAccount] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [challengeEmail, setChallengeEmail] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm<ILoginForm>({
    defaultValues: {
      email: "",
      password: "",
      currentProxy: "",
      pin: "",
      phoneNumber: "",
      phoneCountry: "",
    },
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const getChallengeInfo = (challengeType: string) => {
    switch (challengeType) {
      case "EmailPinChallenge":
        return "Please enter the PIN that you received via email";
      case "SMSPinChallenge":
        return "Please enter the PIN that you received via SMS";
      case "AppChallenge":
        return "Please confirm login on your app";
      case "AuthenticatorAppChallenge":
        return "Please enter the PIN from you authentication app";
      case "PhoneChallenge":
        return "Please enter your phone number to continue";
      default:
        Sentry.setExtra("challenge_type", challengeType);
        Sentry.captureMessage("loginUser - missing challenge handling");
        return "Unknown error occurred. Please try again later";
    }
  };

  function loginUser(email: string, password: string, currentProxy: string) {
    const service = WhiteLabelService();
    const captchaTimeout = setTimeout(() => {
      toast.info(
        "The login process is taking some time, please do not close or refresh this page"
      );
    }, 10000);
    service
      .loginUser(email, password, currentProxy)
      .then((resp) => {
        if (
          resp.login_result === LoginState.PASS &&
          tokenProfileData &&
          resp.profile &&
          tokenProfileData.id !== resp.profile.id
        ) {
          toast.error(
            "Mismatched accounts, please use the required account to reconnect"
          );
          return;
        }
        if (resp.login_result === LoginState.BAD_PASSWORD) {
          toast.error(
            "Wrong password, please use the correct password to connect"
          );
          return;
        }
        setLoginState(resp.login_result);
        if (resp.profile) {
          setProfileData(resp.profile);
        }
        if (resp.challenge_type) {
          setChallengeType(resp.challenge_type);
          if (resp.challenge_type === ChallengeType.AppChallenge) {
            appChallenge(email);
          } else if (resp.challenge_type === ChallengeType.CaptchaChallenge) {
            toast.error("There was a problem logging you in, please try again");
          } else if (resp.challenge_type === ChallengeType.DHomePageGuest) {
            toast.error("We are not able to log you in with this account");
          } else if (resp.challenge_type === ChallengeType.RestrictedChallenge) {
            toast.error(
              "Your LinkedIn account has been restricted. Please go directly to LinkedIn and log in to ensure that your account is still valid. If you encounter difficulties, contact LinkedIn support"
            );
          } else if (resp.challenge_type) {
            toast.info(getChallengeInfo(resp.challenge_type));
          }
        }
      })
      .catch((err) => {
        Sentry.setExtra("error", err);
        Sentry.captureMessage("loginUser - Error");
        toast.error("There was an error logging you in, please try again");
      })
      .finally(() => {
        clearTimeout(captchaTimeout);
        setIsLoading(false);
      });
  }
  function emailPinChallenge(email: string, pin: string) {
    const service = WhiteLabelService();
    service
      .resolveLoginPin(email, pin)
      .then((resp) => {
        setLoginState(resp.login_result);
        setProfileData(resp.profile);
        if (resp.challenge_type) {
          setChallengeType(resp.challenge_type);
          toast.info(getChallengeInfo(resp.challenge_type));
        }
      })
      .catch((err) => {
        Sentry.setExtra("error", err);
        Sentry.captureMessage("resolveLoginPin - Error");
        toast.error("There was an error resolving pin, please try again");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function resolvePhoneChallengeHandler(
    email: string,
    countryCode: string,
    phoneNumber: string
  ) {
    const service = WhiteLabelService();
    service
      .resolvePhoneChallenge(email, countryCode, phoneNumber)
      .then((resp) => {
        setLoginState(resp.login_result);
        if (resp.profile) {
          setProfileData(resp.profile);
        }
        if (resp.challenge_type) {
          setChallengeType(resp.challenge_type);
          toast.info(getChallengeInfo(resp.challenge_type));
        }
      })
      .catch((err) => {
        Sentry.setExtra("error", err);
        Sentry.captureMessage("resolvePhoneChallenge - Error");
        toast.error(
          "There was an error submitting your phone number, please try again"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function appChallenge(email: string) {
    setChallengeEmail(email);
    const service = WhiteLabelService();
    service.fetchLoginChallenge(email).then((resp) => {
      setChallengeToken(resp.token);
    });
  }

  const onSubmit: SubmitHandler<ILoginForm> = (data) => {
    const { email, password, currentProxy, pin } = data;
    setIsLoading(true);
    switch (loginState) {
      case LoginState.ERROR:
        setLoginState(LoginState.LOGIN);
        loginUser(email, password, currentProxy);
        break;
      case LoginState.CHALLENGE:
        if (
          challengeType === ChallengeType.EmailPinChallenge ||
          challengeType === ChallengeType.SMSPinChallenge ||
          challengeType === ChallengeType.AuthenticatorAppChallenge
        ) {
          emailPinChallenge(email, pin);
        } else if (challengeType === ChallengeType.PhoneChallenge) {
          const strippedPhone = data.phoneNumber
            .replace(/\D/g, "")
            .replace(/^0/, "");
          resolvePhoneChallengeHandler(
            email,
            data.phoneCountry.toLowerCase(),
            strippedPhone
          );
        } else {
          loginUser(email, password, currentProxy);
        }
        break;
      case LoginState.LOGIN:
        loginUser(email, password, currentProxy);
        break;
      case LoginState.PASS:
      default:
        break;
    }
  };
  const onInvalid: SubmitErrorHandler<ILoginForm> = (error) => {
    console.error(error);
  };

  //Get whitelabel customization
  useEffect(() => {
    async function getCustomization() {
      const service = WhiteLabelService();
      const customization = await service.getCustomerCustomizations();
      setCustomization(customization);
      document.title = customization.name;
      setWorkspaceId(customization.workspace_id);
      document
        .getElementById("favicon")
        ?.setAttribute("href", customization.favicon);
    }

    getCustomization().catch((error) => {
      Sentry.setExtra("error", error);
      Sentry.captureMessage("getCustomization - Error");
      toast.error("There was an error getting customization, please try again");
    });
  }, [setCustomization]);

  const getProxy = React.useCallback(async () => {
    const service = WhiteLabelService();
    const px = await service.getAvailableProxies();
    px.countries = px.countries
      .map((i: string) => {
        return {
          code: i,
          name: getCountryName(i),
        };
      })
      .sort((a: Country, b: Country) => {
        return ("" + a.name).localeCompare(b.name);
      });
    setProxies(px);
    const seededCountry =
      px.country_code && px.country_code.length === 2
        ? px.country_code.toUpperCase()
        : "";
    reset({
      currentProxy: px.country_code,
      phoneCountry: seededCountry,
    });
  }, [reset]);
  //Get proxy countries
  useEffect(() => {
    getProxy().catch((error) => {
      console.error(error);
      Sentry.setExtra("error", error);
      Sentry.captureMessage("getProxy - Error");
      toast.error("There was an error getting the proxies, please try again");
    });
  }, [getProxy]);

  const getAccount = React.useCallback(async () => {
    const accId = await getAccountFromToken();
    if (!accId) return;
    const service = WhiteLabelService();
    const acc = await service.getAccount(accId);
    setProfileData(acc);
    setTokenProfileData(acc);
    reset({
      email: acc.email,
    });
  }, [reset, setProfileData]);
  //GET Account
  useEffect(() => {
    getAccount().catch((error) => {
      Sentry.setExtra("error", error);
      Sentry.captureMessage("getAccount - Error");
      toast.error("There was an error getting the account, please try again");
    });
  }, [getAccount]);

  function checkIsLogin() {
    const isLogin = loginState === LoginState.LOGIN;
    const isCaptcha =
      loginState === LoginState.CHALLENGE &&
      challengeType === ChallengeType.CaptchaChallenge;
    const isHomepageGuestHome =
      loginState === LoginState.CHALLENGE &&
      challengeType === ChallengeType.DHomePageGuest;
    const isRestricted =
      loginState === LoginState.CHALLENGE &&
      challengeType === ChallengeType.RestrictedChallenge;
    return isLogin || isCaptcha || isHomepageGuestHome || isRestricted;
  }
  function checkIsEmailPin() {
    const isPin =
      loginState === LoginState.CHALLENGE &&
      (challengeType === ChallengeType.EmailPinChallenge ||
        challengeType === ChallengeType.SMSPinChallenge ||
        challengeType === ChallengeType.AuthenticatorAppChallenge);
    return isPin;
  }
  function checkIsAppChallenge() {
    const isAppChallenge =
      loginState === LoginState.CHALLENGE &&
      challengeType === ChallengeType.AppChallenge;
    return isAppChallenge;
  }
  function checkIsPhoneChallenge() {
    return (
      loginState === LoginState.CHALLENGE &&
      challengeType === ChallengeType.PhoneChallenge
    );
  }
  const { connectLinkedInAccount } = LoginExtension();
  const connectAccount = useCallback(
    (accountId: string) => {
      connectLinkedInAccount(accountId)
        .then(() => {
          toast.success("Account connected");
          setLoginState(LoginState.PASS);
        })
        .catch((err) => {
          Sentry.setExtra("error", err);
          Sentry.captureMessage("connectLinkedinAccount - Error");
          console.log(err.code);
          if (err.code === 409) {
            toast.error(
              "This account is already connected to a different workspace"
            );
          } else if (err.code === 401) {
            toast.error(
              "Please make sure you are logged into your LinkedIn account"
            );
          } else {
            toast.error("Failed to log in with extension. Try to log out and log into Linkedin again to get a new session");
          }
        })
        .finally(() => {
          setIsConnectingAccount(false);
        });
    },
    [connectLinkedInAccount]
  );

  const handleConnectLinkedInAccount = useCallback(async (): Promise<void> => {
    setIsConnectingAccount(true);
    connectAccount(profileData.id.toString());
  }, [connectAccount, profileData]);

  const handleChallengeSuccess = useCallback(
    (profile: ProfileData) => {
      setProfileData(profile);
      connectAccount(profile.id);
    },
    [connectAccount]
  );
  function handleChallengeFail() {
    console.error("challenge fail");
  }

  const connect = useCallback(
    (email: string, token: string) => {
      const websocketUrl = `${wsBaseUrl}/workspaces/${workspaceId}/accounts/challenge/${email}/status?authorization=${token}`;

      const websocket = new WebSocket(websocketUrl);

      websocket.onmessage = (event) => {
        const {
          challenge_status: challengeStatus,
          status,
          error,
          profile,
        } = JSON.parse(event.data);

        if (challengeStatus === "SOLVED") {
          handleChallengeSuccess(profile);
        }

        if (status === "fail" || challengeStatus === "FAILED") {
          Sentry.setExtra("error", error);
          Sentry.setExtra("data_json", JSON.stringify(event.data));
          Sentry.captureMessage("This was me challenge - WebSocket Error");
          handleChallengeFail();
        }
      };
      return websocket;
    },
    [workspaceId, handleChallengeSuccess]
  );

  useEffect(() => {
    if (challengeToken) {
      connect(challengeEmail, challengeToken);
      setChallengeEmail(null);
      setChallengeToken(null);
    }
  }, [challengeEmail, challengeToken, connect]);

  function invertColor(hex: string) {
    if (!hex) {
      return "#000000";
    }
    if (hex.indexOf("#") === 0) {
      hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length > 6) {
      hex = hex[0] + hex[1] + hex[2] + hex[3] + hex[4] + hex[5];
    }
    const r = parseInt(hex.slice(0, 2), 16),
      g = parseInt(hex.slice(2, 4), 16),
      b = parseInt(hex.slice(4, 6), 16);

    // https://stackoverflow.com/a/3943023/112731
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000000" : "#FFFFFF";
  }

  function handleBackToEmail() {
    setIsConnectingAccount(false);
    const resetData = {
      currentProxy: proxies.country_code,
      email: "",
    };
    setLoginType(LoginType.EMAIL);
    setLoginState(LoginState.LOGIN);
    setProfileData(tokenProfileData);
    resetData.email = tokenProfileData ? tokenProfileData.email : "";

    reset(resetData);
  }

  function handleLoginWithExtension() {
    setLoginType(LoginType.SELECT_PROXY);
  }

  function renderConfirmButtons() {
    return (
      <>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} textAlign="left">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            className="wlb-button secondary"
            onClick={handleBackToEmail}
          >
            Back
          </LoadingButton>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} textAlign="right">
          <LoadingButton
            type="submit"
            variant="contained"
            className="wlb-button"
            sx={{
              backgroundColor: customization?.primary_color ?? "#000000",
              color: invertColor(customization?.primary_color ?? "#000000"),
            }}
            onClick={handleConnectLinkedInAccount}
            loading={isConnectingAccount}
          >
            Connect
          </LoadingButton>
        </Grid>
      </>
    );
  }

  const [open, setOpen] = React.useState(false);

  const handleTooltipClose = (isMobile: boolean) => {
    if (!isMobile) setOpen(false);
  };

  const handleTooltipOpen = (isMobile: boolean) => {
    if (!isMobile) setOpen(true);
  };

  function renderLoginButtons() {
    if (loginState === LoginState.PASS) {
      return null;
    }
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isChromeDesktop = navigator.userAgent.includes("Chrome") && !isMobile;

    return (
      <>
        {customization?.use_chrome_extension && (
          <>
            {loginType === LoginType.SELECT_PROXY && (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} textAlign="left">
                <LoadingButton
                  loading={isLoading}
                  type="submit"
                  variant="contained"
                  className="wlb-button secondary"
                  onClick={handleBackToEmail}
                >
                  Back
                </LoadingButton>
              </Grid>
            )}
            {loginType !== LoginType.EXTENSION && (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} textAlign="center">
                {!isChromeDesktop ? (
                  <Tooltip
                    arrow
                    open={isMobile || open}
                    onOpen={() => handleTooltipOpen(isMobile)}
                    onClose={() => handleTooltipClose(isMobile)}
                    placement="top"
                    title="Your browser does not support the login extension. Please download desktop Google Chrome to use this feature"
                  >
                    <span>
                      <ExtensionLogin
                        disabled={!isChromeDesktop}
                        setAccount={setProfileData}
                        extensionId={customization.extension_id}
                        setLoginType={setLoginType}
                        countryCode={getValues("currentProxy")}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <>
                    {loginType === LoginType.SELECT_PROXY ? (
                      <ExtensionLogin
                        disabled={!isChromeDesktop}
                        setAccount={setProfileData}
                        extensionId={customization.extension_id}
                        setLoginType={setLoginType}
                        countryCode={getValues("currentProxy")}
                      />
                    ) : (
                      <LoadingButton
                        variant="contained"
                        className="wlb-button secondary"
                        onClick={handleLoginWithExtension}
                      >
                        Login via Extension
                      </LoadingButton>
                    )}
                  </>
                )}
              </Grid>
            )}
          </>
        )}
        {loginType !== LoginType.SELECT_PROXY && (
          <Grid
            size={{
              xs: 12,
              sm: customization?.use_chrome_extension ? 6 : 12,
              md: customization?.use_chrome_extension ? 6 : 12,
            }}
            textAlign="right"
          >
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="text"
              className="wlb-button"
              sx={{
                backgroundColor: customization?.primary_color ?? "#000000",
                color: invertColor(customization?.primary_color ?? "#000000"),
              }}
            >
              {loginState === "LOGIN" ? "Login" : "Submit"}
            </LoadingButton>
          </Grid>
        )}
      </>
    );
  }

  return (
    <>
      <div id="login-wrapper">
        {customization?.logo && (
          <img src={customization?.logo} alt="logo" className="logo" />
        )}
        {loginType === LoginType.EMAIL ? (
          loginState === LoginState.PASS ? (
            <h2>Account connected to {customization?.name}</h2>
          ) : checkIsEmailPin() ? (
            <>
              <h2>Enter your PIN</h2>
              <h4>
                You received a Pin from LinkedIn via SMS, Email or Authenticator
                App. Please enter it below to connect your LinkedIn Account
              </h4>
            </>
          ) : checkIsPhoneChallenge() ? (
            <h2>Enter your phone number to confirm it's you</h2>
          ) : (
            <h2>Connect a new LinkedIn account to {customization?.name}</h2>
          )
        ) : loginState === LoginState.PASS ? (
          <h2>Account connected to {customization?.name}</h2>
        ) : (
          <>
            <h2>Connect a new LinkedIn account to {customization?.name}</h2>
            {!profileData && (
              <>
                <h2>Select a proxy for your account</h2>
              </>
            )}
            <h4>Login to get access to Campaigns, Leads and Inbox</h4>
          </>
        )}

        {profileData && profileData.id && (
          <>
            <Grid size={12}>
              <Profile profile={profileData} />
            </Grid>
          </>
        )}
        <form
          action=""
          id="loginForm"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          <Grid container size={12} spacing={2}>
            {checkIsLogin() && (
              <>
                {loginType === LoginType.EMAIL && (
                  <>
                    <Grid size={12}>
                      <FormControl fullWidth className="wlb-control">
                        <label htmlFor="email">Email address</label>
                        <OutlinedInput
                          id="email"
                          placeholder="Email"
                          className="wlb-input"
                          error={typeof errors.email !== "undefined"}
                          {...register("email", {
                            required: "Email is required",
                          })}
                        />
                        {errors.email && (
                          <FormHelperText className="Mui-error">
                            {errors.email.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <FormControl fullWidth className="wlb-control">
                        <label htmlFor="password">Password</label>
                        <OutlinedInput
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          className="wlb-input"
                          error={typeof errors.password !== "undefined"}
                          {...register("password", {
                            required: "Password is required",
                          })}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? "hide the password"
                                    : "display the password"
                                }
                                onClick={handleClickShowPassword}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseUp={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOutlined />
                                ) : (
                                  <VisibilityOffOutlined />
                                )}
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                        {errors.password && (
                          <FormHelperText className="Mui-error">
                            {errors.password.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </>
                )}
                {loginType !== LoginType.EXTENSION && (
                  <Grid size={12}>
                    <FormControl fullWidth className="wlb-control">
                      <label id="proxy-label">Proxy</label>
                      <Controller
                        name="currentProxy"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="proxy-label"
                            id="proxy"
                            className="wlb-input"
                            sx={{ textAlign: "left" }}
                            MenuProps={{
                              PaperProps: { sx: { maxHeight: 250 } },
                            }}
                          >
                            {proxies.custom_proxies?.length > 0 && (
                              <MenuItem disabled>Custom proxies</MenuItem>
                            )}
                            {proxies.custom_proxies?.map((proxy) => {
                              return (
                                <MenuItem key={proxy.id} value={proxy.id}>
                                  <ReactCountryFlag
                                    style={{ marginRight: ".5rem" }}
                                    countryCode={proxy.location?.countryISO}
                                  />
                                  {proxy.name}
                                  {proxies.country_code ===
                                    proxy.location?.countryISO && (
                                    <Chip
                                      label="recommended"
                                      variant="outlined"
                                      size="small"
                                      className="wlb-select-chip"
                                      sx={{
                                        float: "right",
                                        marginLeft: "auto",
                                      }}
                                    />
                                  )}
                                </MenuItem>
                              );
                            })}
                            {proxies.custom_proxies?.length > 0 && (
                              <MenuItem disabled>Global Proxies</MenuItem>
                            )}
                            {proxies.countries.map((country) => {
                              return (
                                <MenuItem
                                  key={country.code}
                                  value={country.code}
                                >
                                  <ReactCountryFlag
                                    style={{ marginRight: ".5rem" }}
                                    countryCode={country.code}
                                  />
                                  {country.name}
                                  {proxies.country_code === country.code && (
                                    <Chip
                                      label="recommended"
                                      variant="outlined"
                                      size="small"
                                      className="wlb-select-chip"
                                      sx={{
                                        float: "right",
                                        marginLeft: "auto",
                                      }}
                                    />
                                  )}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      ></Controller>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}
            {checkIsEmailPin() && (
              <>
                <Grid size={12}>
                  <FormControl fullWidth className="wlb-control">
                    <OutlinedInput
                      id="pin"
                      label="PIN"
                      className="wlb-input"
                      error={typeof errors.pin !== "undefined"}
                      {...register("pin", { required: "Pin is required" })}
                    />
                    {errors.pin && (
                      <FormHelperText className="Mui-error">
                        {errors.pin.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </>
            )}
            {checkIsAppChallenge() && (
              <>
                {profileData === undefined && (
                  <>
                    <div className="wlb-app-challenge">
                      Please check your phone for login confirmation
                    </div>
                  </>
                )}
              </>
            )}
            {checkIsPhoneChallenge() && (
              <Grid size={12}>
                <FormControl fullWidth className="wlb-control">
                  <label htmlFor="phoneNumber">Phone number</label>
                  <OutlinedInput
                    id="phoneNumber"
                    placeholder="Phone number"
                    className="wlb-input"
                    error={typeof errors.phoneNumber !== "undefined"}
                    {...register("phoneNumber", {
                      required: "Phone number is required",
                      validate: (value) => {
                        const digits = value.replace(/\D/g, "").replace(/^0/, "");
                        return digits.length > 0 || "Phone number is required";
                      },
                    })}
                    startAdornment={
                      <InputAdornment position="start">
                        <Controller
                          name="phoneCountry"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              variant="standard"
                              disableUnderline
                              displayEmpty
                              inputProps={{ "aria-label": "Country dial code" }}
                              sx={{ minWidth: 110, marginRight: 1 }}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <span style={{ opacity: 0.5 }}>Country</span>;
                                }
                                const country = countries.find(
                                  (c) => c.code === selected
                                );
                                return (
                                  <span>
                                    <ReactCountryFlag
                                      countryCode={selected as string}
                                      style={{ marginRight: ".4rem" }}
                                    />
                                    {country?.dial_code ?? ""}
                                  </span>
                                );
                              }}
                              MenuProps={{
                                PaperProps: { sx: { maxHeight: 250 } },
                              }}
                            >
                              {countries.map((country) => (
                                <MenuItem key={country.code} value={country.code}>
                                  <ReactCountryFlag
                                    countryCode={country.code}
                                    style={{ marginRight: ".5rem" }}
                                  />
                                  {country.name} ({country.dial_code})
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </InputAdornment>
                    }
                  />
                  {errors.phoneNumber && (
                    <FormHelperText className="Mui-error">
                      {errors.phoneNumber.message}
                    </FormHelperText>
                  )}
                  {errors.phoneCountry && (
                    <FormHelperText className="Mui-error">
                      Country is required
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            <div id="form-actions">
              {!checkIsAppChallenge() && (
                <>
                  {loginType === LoginType.EXTENSION &&
                  loginState !== LoginState.PASS
                    ? renderConfirmButtons()
                    : renderLoginButtons()}
                </>
              )}
              <div className="wlb-security-declaration">
                <LockIcon sx={{ fontSize: 16 }}></LockIcon>
                <span>Secured with TLS 1.3 encryption</span>
              </div>
            </div>
          </Grid>
        </form>
      </div>
    </>
  );
}
